import {
  FindCompCriteriaTabs,
  FindCompTabs,
  FindCompTabsViews,
} from 'constants/findCompTabs';

export const ID_PATH_PARAM = ':id';
export const COMPANY_ID_PATH_PARAM = ':companyId';
export const PROPERTIES_ID_PATH_PARAM = ':propertyId';
export const COMP_TYPE_PATH_PARAM = ':type(lease|sale|property|tim)';
export const ABSTRACTION_TYPE_PATH_PARAM = ':type(leases|sales)';
export const SUBMARKET_TYPE_PARAM = ':propertyTypeId';
export const FIND_COMPS_TAB_PARAM = 't';
export const FIND_COMPS_CRITERIA_TAB_PARAM = 'ct';
export const FIND_COMPS_TAB_VIEW_PARAM = 'tv';
export const REF_REDIRECT_PARAM = 'ref';
export const FIND_COMPS_ADVANCED_FILTER = 'af';
export const TABLEAU_DASHBOARD_PARAM = 'tdp';

export type FindCompsParam = {
  criteriaActiveTab?: FindCompCriteriaTabs;
  resultActiveTab?: FindCompTabs;
  urlParams?: string;
  activeTabView?: FindCompTabsViews;
  showAdvancedFilter?: boolean;
};

export type TableauParam = {
  dashboardId?: number;
};

export const routes = {
  rootPath: '/',
  loginFormPath: '/login',
  msOauthPath: '/oauth2',
  loginSsoPath: '/login-sso',
  loginEmailPath: '/login-email',
  propertiesPath: '/properties',
  newCompsPath: '/comps/new',
  uploadCompsPath: '/comps/upload',
  userSettingsPath: '/user-settings',
  scoopsPathNew: '/scoops/new',
  findCompsPath: '/comps',
  showCompsPath: '/comps',
  companiesPath: '/companies',
  tenantTourPath: '/tenant-tours',
  compSetPath: '/comp-set',
  compSetsPath: '/comp-sets',
  editCompsPath: '/comps/edit',
  exploreActivityPath: '/activity',
  welcomeWizardPath: '/welcome',
  tableauDashboardsPath: '/market-analytics',
  oktaCallbackPath: '/okta/callback',
  submarketsPath: '/submarkets',
  marketsPath: '/markets',
  projectsPath: '/projects',
  addressProfilePath: '/address',
  abstractionPath: '/abstraction',
  notFoundPath: '/not-found',
} as const;

const locations = {
  root: () => routes.rootPath,
  loginMSCallback: () => routes.msOauthPath,
  loginOktaCallback: () => routes.oktaCallbackPath,
  loginMSPage: () => routes.loginSsoPath,
  loginEmail: () => routes.loginEmailPath,
  loginForm: () => routes.loginFormPath,
  uploadComps: () => routes.uploadCompsPath,
  newComps: () => routes.newCompsPath,
  userSettings: () => routes.userSettingsPath,
  showProperty: (id: number | string, section?: string) => {
    const BASE_PATH = `${routes.propertiesPath}/${id}`;

    return section ? `${BASE_PATH}/${section}` : `${BASE_PATH}`;
  },
  newScoop: () => routes.scoopsPathNew,
  findComps: (params?: FindCompsParam) => {
    const tabParams = [];

    if (params?.resultActiveTab) {
      tabParams.push(
        `${FIND_COMPS_TAB_PARAM}=${params?.resultActiveTab || ''}`,
      );
    }

    if (params?.criteriaActiveTab) {
      tabParams.push(
        `${FIND_COMPS_CRITERIA_TAB_PARAM}=${params?.criteriaActiveTab || ''}`,
      );
    }

    if (params?.activeTabView) {
      tabParams.push(
        `${FIND_COMPS_TAB_VIEW_PARAM}=${params?.activeTabView || ''}`,
      );
    }

    if (params?.showAdvancedFilter) {
      tabParams.push(`${FIND_COMPS_ADVANCED_FILTER}=t`);
    }

    if (params?.urlParams) {
      tabParams.push(params?.urlParams);
    }

    let urlParams = '';
    if (tabParams.length) {
      urlParams = `?${tabParams.join('&')}`;
    }

    return routes.findCompsPath + urlParams;
  },
  showCompany: (id: number | string, section?: string) => {
    const companyBasePath = `${routes.companiesPath}/${id}`;

    return section ? `${companyBasePath}/${section}` : companyBasePath;
  },
  showTenantTours: (companyId: number | string) =>
    `${routes.companiesPath}/${companyId}${routes.tenantTourPath}`,
  showTenantTour: (
    companyId: number | string,
    id: number | string,
    section?: string,
  ) => {
    const tenantTourPath = `${routes.companiesPath}/${companyId}${routes.tenantTourPath}/${id}`;

    if (section) {
      return `${tenantTourPath}/${section}`;
    }

    return tenantTourPath;
  },
  showComps: (id: number | string, type: string) =>
    `${routes.showCompsPath}/${id}/${type}`,
  editComps: (id: number | string, type: string) =>
    `${routes.editCompsPath}/${id}/${type}`,
  exploreActivity: (urlParams?: string) => {
    return urlParams
      ? `${routes.exploreActivityPath}?${urlParams}`
      : routes.exploreActivityPath;
  },
  welcomeWizard: () => routes.welcomeWizardPath,
  showProjects: () => routes.projectsPath,
  marketAnalytics: (params?: TableauParam) => {
    if (params?.dashboardId) {
      return `${routes.tableauDashboardsPath}?${TABLEAU_DASHBOARD_PARAM}=${params?.dashboardId}`;
    }

    return routes.tableauDashboardsPath;
  },
  showSubmarket: (
    id: number | string,
    propertyTypeId: number | string,
    section?: string,
  ) => {
    const submarketPath = `${routes.submarketsPath}/${id}/${propertyTypeId}`;

    if (section) {
      return `${submarketPath}/${section}`;
    }

    return submarketPath;
  },
  showMarket: (id: number | string, section?: string) => {
    const marketPath = `${routes.marketsPath}/${id}`;

    if (section) {
      return `${marketPath}/${section}`;
    }

    return marketPath;
  },
  showTenantTourCreationPage: () => `${routes.tenantTourPath}/new`,
  goToCompSet: (propertyId: number | string) =>
    `${routes.propertiesPath}/${propertyId}${routes.compSetPath}`,
  showPropertyCompSets: (propertyId: number | string) =>
    `${routes.propertiesPath}/${propertyId}${routes.compSetsPath}`,
  showPropertyCompSet: (
    propertyId: number | string,
    id: number | string,
    section?: string,
  ) => {
    const compSetPath = `${routes.propertiesPath}/${propertyId}${routes.compSetsPath}/${id}`;

    if (section) {
      return `${compSetPath}/${section}`;
    }

    return compSetPath;
  },
  showAddressProfileAmenities: (urlParams?: string) =>
    urlParams
      ? `${routes.addressProfilePath}/nearby-amenities${urlParams}`
      : `${routes.addressProfilePath}/nearby-amenities`,
  showAddressProfileDemographics: (urlParams?: string) =>
    urlParams
      ? `${routes.addressProfilePath}/demographics${urlParams}`
      : `${routes.addressProfilePath}/demographics`,
  abstraction: (type: string) => `${routes.abstractionPath}/${type}`,
  showNotFoundPage: () => routes.notFoundPath,
};

export default locations;
